import { useState } from "react";
import { Navigate } from "react-router-dom";
import { useRole } from "@/hooks/useRole";
import { PageHeader } from "@/components/layout/PageHeader";
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from "@/components/ui/resizable";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { AgentFileTree } from "@/components/scratchpad/AgentFileTree";
import { AgentFileEditor } from "@/components/scratchpad/AgentFileEditor";
import { PreviewPane } from "@/components/scratchpad/PreviewPane";

export default function ScratchpadPage() {
  const { isAdmin, isLoading } = useRole();

  const [selectedFileId, setSelectedFileId] = useState<string | null>(null);
  const [isDirty, setIsDirty] = useState(false);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  return (
    <main className="w-full h-[calc(100vh-4rem)] flex flex-col">
      <PageHeader title="Agent Builder" subtitle="Author agent workflows with tiered skills." />

      {/* ── Desktop: horizontal resizable panels (md and up) ──────────────── */}
      <div className="flex-1 min-h-0 hidden md:flex">
        <ResizablePanelGroup direction="horizontal" className="h-full">
          {/* Left pane — Tree */}
          <ResizablePanel defaultSize={22} minSize={15} className="glass-panel overflow-hidden">
            <AgentFileTree
              selectedFileId={selectedFileId}
              onSelect={setSelectedFileId}
              isDirty={isDirty}
            />
          </ResizablePanel>

          <ResizableHandle withHandle />

          {/* Middle pane — Editor (placeholder, Agent 3 fills in) */}
          <ResizablePanel defaultSize={50} minSize={30} className="overflow-hidden">
            <AgentFileEditor
              selectedFileId={selectedFileId}
              onDirtyChange={setIsDirty}
              onDeleted={() => setSelectedFileId(null)}
            />
          </ResizablePanel>

          <ResizableHandle withHandle />

          {/* Right pane — Preview (placeholder, Agent 4 fills in) */}
          <ResizablePanel defaultSize={28} minSize={20} className="glass-panel overflow-hidden">
            <PreviewPane selectedFileId={selectedFileId} />
          </ResizablePanel>
        </ResizablePanelGroup>
      </div>

      {/* ── Mobile: stacked accordions (below md) ─────────────────────────── */}
      <div className="flex-1 min-h-0 md:hidden overflow-y-auto">
        <Accordion type="single" collapsible defaultValue="tree" className="w-full">
          <AccordionItem value="tree" className="border-b border-border">
            <AccordionTrigger className="px-4 py-3 text-sm font-medium hover:no-underline">
              Tree
            </AccordionTrigger>
            <AccordionContent className="p-0">
              <div className="min-h-[300px]">
                <AgentFileTree
                  selectedFileId={selectedFileId}
                  onSelect={setSelectedFileId}
                  isDirty={isDirty}
                />
              </div>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="editor" className="border-b border-border">
            <AccordionTrigger className="px-4 py-3 text-sm font-medium hover:no-underline">
              Editor
            </AccordionTrigger>
            <AccordionContent className="p-0">
              <div className="min-h-[400px]">
                <AgentFileEditor
                  selectedFileId={selectedFileId}
                  onDirtyChange={setIsDirty}
                  onDeleted={() => setSelectedFileId(null)}
                />
              </div>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="preview">
            <AccordionTrigger className="px-4 py-3 text-sm font-medium hover:no-underline">
              Preview
            </AccordionTrigger>
            <AccordionContent className="p-0">
              <div className="min-h-[300px]">
                <PreviewPane selectedFileId={selectedFileId} />
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
    </main>
  );
}
